/**
 * Narration Scripts
 * All voice narration text for activities in English and Spanish
 */

import { VoiceEmotion } from './voiceManager';

export interface NarrationScript {
  id: string;
  en: string;
  es: string;
  emotion: VoiceEmotion;
  pauseAfter: number;
  soundEffect?: string;
}

export const narrationScripts = {
  // Welcome & Intro
  welcome: {
    id: 'welcome',
    en: 'Hello, little friend! Welcome to Wonderpath EcoGames!',
    es: '¡Hola, amiguito! ¡Bienvenido a Wonderpath EcoGames!',
    emotion: 'excited' as VoiceEmotion,
    pauseAfter: 2000,
  },

  // Animal Journey - Bunny Level
  animalJourney: {
    welcome: {
      id: 'animal_journey_welcome',
      en: 'Hello, little friend! Welcome to Animal Friends Journey!',
      es: '¡Hola, amiguito! ¡Bienvenido al Viaje de Amigos Animales!',
      emotion: 'excited' as VoiceEmotion,
      pauseAfter: 2000,
    },
    bunnyIntro: {
      id: 'bunny_intro',
      en: "Look! It's Benny the Bunny! Benny needs to get home to his cozy burrow.",
      es: '¡Mira! ¡Es Beni el Conejito! Beni necesita llegar a su madriguera cómoda.',
      emotion: 'gentle' as VoiceEmotion,
      pauseAfter: 2000,
    },
    bunnyFirstHop: {
      id: 'bunny_first_hop',
      en: 'Great job! Benny hopped! Keep tapping to help Benny hop more!',
      es: '¡Muy bien! ¡Beni brincó! ¡Sigue tocando para ayudar a Beni a brincar más!',
      emotion: 'excited' as VoiceEmotion,
      pauseAfter: 1000,
    },
    bunnyComplete: {
      id: 'bunny_complete',
      en: 'Yay! You helped Benny get home! Great job, friend!',
      es: '¡Yupi! ¡Ayudaste a Beni a llegar a casa! ¡Muy bien, amigo!',
      emotion: 'celebrate' as VoiceEmotion,
      pauseAfter: 2000,
    },
  },

  // Feelings Garden
  feelingsGarden: {
    welcome: {
      id: 'feelings_welcome',
      en: 'Hello, little gardener! Welcome to the Feelings Garden!',
      es: '¡Hola, pequeño jardinero! ¡Bienvenido al Jardín de Sentimientos!',
      emotion: 'gentle' as VoiceEmotion,
      pauseAfter: 2000,
    },
    happyFlowerIntro: {
      id: 'happy_flower_intro',
      en: 'This is Happy Flower! She loves sunshine and water!',
      es: '¡Esta es la Flor Feliz! ¡Le encanta el sol y el agua!',
      emotion: 'excited' as VoiceEmotion,
      pauseAfter: 2000,
    },
    sadFlowerIntro: {
      id: 'sad_flower_intro',
      en: 'Oh no, this flower is feeling sad. How can we help her feel better?',
      es: 'Oh no, esta flor se siente triste. ¿Cómo podemos ayudarla a sentirse mejor?',
      emotion: 'gentle' as VoiceEmotion,
      pauseAfter: 2000,
    },
    waterPlant: {
      id: 'water_plant',
      en: 'Great! You watered the plant! Look how happy it is!',
      es: '¡Genial! ¡Regaste la planta! ¡Mira qué feliz está!',
      emotion: 'excited' as VoiceEmotion,
      pauseAfter: 1500,
    },
    plantGrowing: {
      id: 'plant_growing',
      en: 'Look! The plant is growing bigger and happier!',
      es: '¡Mira! ¡La planta está creciendo más grande y más feliz!',
      emotion: 'excited' as VoiceEmotion,
      pauseAfter: 1500,
    },
  },

  // Color the Habitat
  colorHabitat: {
    welcome: {
      id: 'color_welcome',
      en: "Let's bring nature to life with colors!",
      es: '¡Démosle vida a la naturaleza con colores!',
      emotion: 'excited' as VoiceEmotion,
      pauseAfter: 2000,
    },
    chooseColor: {
      id: 'choose_color',
      en: 'Tap a color, then tap what you want to paint!',
      es: '¡Toca un color y luego toca lo que quieres pintar!',
      emotion: 'normal' as VoiceEmotion,
      pauseAfter: 2000,
    },
    greatChoice: {
      id: 'great_choice',
      en: 'Beautiful! What a great color choice!',
      es: '¡Hermoso! ¡Qué gran elección de color!',
      emotion: 'excited' as VoiceEmotion,
      pauseAfter: 1000,
    },
    complete: {
      id: 'color_complete',
      en: 'Wow! You made the forest so colorful and beautiful!',
      es: '¡Wow! ¡Hiciste el bosque tan colorido y hermoso!',
      emotion: 'celebrate' as VoiceEmotion,
      pauseAfter: 2000,
    },
  },

  // General feedback
  encouragement: {
    tryAgain: {
      id: 'try_again',
      en: "That's okay! Let's try again!",
      es: '¡Está bien! ¡Intentemos de nuevo!',
      emotion: 'gentle' as VoiceEmotion,
      pauseAfter: 1000,
    },
    greatJob: {
      id: 'great_job',
      en: 'Great job!',
      es: '¡Muy bien!',
      emotion: 'excited' as VoiceEmotion,
      pauseAfter: 500,
    },
    youDidIt: {
      id: 'you_did_it',
      en: 'You did it!',
      es: '¡Lo lograste!',
      emotion: 'celebrate' as VoiceEmotion,
      pauseAfter: 1000,
    },
  },
};

/**
 * Get narration script by ID
 */
export function getNarration(scriptId: string): NarrationScript | undefined {
  // Deep search through nested scripts
  const findScript = (obj: any): NarrationScript | undefined => {
    for (const key in obj) {
      if (obj[key].id === scriptId) {
        return obj[key];
      }
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        const result = findScript(obj[key]);
        if (result) return result;
      }
    }
    return undefined;
  };

  return findScript(narrationScripts);
}
